import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { useThemeStore } from './stores/themeStore';
import { useSubscriptionStore } from './stores/subscriptionStore';
import { authApi } from './api/auth';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { SharedLinksPage } from './pages/SharedLinksPage';
import { PublicDownloadPage } from './pages/PublicDownloadPage';
import { PublicUploadPage } from './pages/PublicUploadPage';
import { ClientsPage } from './pages/ClientsPage';
import { ClientDetailPage } from './pages/ClientDetailPage';
import { PublicPortalPage } from './pages/PublicPortalPage';
import { DiagnosticsPage } from './pages/DiagnosticsPage';
import { DiagnosticDetailPage } from './pages/DiagnosticDetailPage';
import { DiagnosticResponsesPage } from './pages/DiagnosticResponsesPage';
import { DiagnosticAnalysisPage } from './pages/DiagnosticAnalysisPage';
import { PublicFormPage } from './pages/PublicFormPage';
import { DocumentEditorPage } from './pages/DocumentEditorPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AdminUserDetailPage } from './pages/admin/AdminUserDetailPage';
import { MainLayout } from './components/layout/MainLayout';
import { Toaster } from './components/ui/Toaster';
import { SubscriptionPendingPage } from './components/SubscriptionPendingPage';
import { Spinner } from './components/ui/Spinner';

const MAX_RETRIES = 3;

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const status = useSubscriptionStore((s) => s.status);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && !hasCheckedStatus) {
      const attemptFetch = async () => {
        try {
          setFetchError(null);
          await Promise.all([
            useSubscriptionStore.getState().fetchStatus(),
            authApi.me().then(({ user }) => {
              useAuthStore.getState().updateUser(user);
            }),
          ]);
          setHasCheckedStatus(true);
        } catch (err) {
          console.error('fetchStatus failed:', err);

          if (retryCount < MAX_RETRIES) {
            // Exponential backoff: 1s, 2s, 4s
            const delayMs = Math.pow(2, retryCount) * 1000;
            setTimeout(() => {
              setRetryCount((c) => c + 1);
            }, delayMs);
          } else {
            // After max retries, show error but allow continue with cached data
            setFetchError('Failed to verify subscription status');
            setHasCheckedStatus(true);
          }
        }
      };

      attemptFetch();
    }
  }, [isAuthenticated, hasCheckedStatus, retryCount]);

  // Refresh when returning to the tab
  useEffect(() => {
    if (!isAuthenticated || !hasCheckedStatus) return;

    const handleFocus = () => {
      // Reset retry count on focus and attempt refresh
      setRetryCount(0);
      useSubscriptionStore.getState().fetchStatus().catch(() => {});
      authApi.me().then(({ user }) => {
        useAuthStore.getState().updateUser(user);
      }).catch(() => {});
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isAuthenticated, hasCheckedStatus]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Show loading only on initial subscription check
  if (!hasCheckedStatus) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white dark:bg-slate-900">
        <Spinner size="lg" />
        {retryCount > 0 && (
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Reintentando... ({retryCount}/{MAX_RETRIES})
          </p>
        )}
      </div>
    );
  }

  // Log warning if fetch failed but continuing with cached data
  if (fetchError) {
    console.warn('Continuing with cached subscription data:', fetchError);
  }

  // If user has pending subscription, show the pending page
  if (status === 'pending') {
    return <SubscriptionPendingPage />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export function App() {
  const initializeTheme = useThemeStore((s) => s.initializeTheme);

  useEffect(() => {
    initializeTheme();
  }, [initializeTheme]);

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/share/:token" element={<PublicDownloadPage />} />
        <Route path="/upload/:token" element={<PublicUploadPage />} />
        <Route path="/p/:token" element={<PublicPortalPage />} />
        <Route path="/f/:token" element={<PublicFormPage />} />

        {/* Document editor (full page, no sidebar) */}
        <Route
          path="/documents/:fileId"
          element={
            <ProtectedRoute>
              <DocumentEditorPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes with sidebar */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/folder/:folderId" element={<DashboardPage />} />
                  <Route path="/starred" element={<DashboardPage />} />
                  <Route path="/trash" element={<DashboardPage />} />
                  <Route path="/links" element={<SharedLinksPage />} />
                  <Route path="/diagnostics" element={<DiagnosticsPage />} />
                  <Route path="/diagnostics/:id" element={<DiagnosticDetailPage />} />
                  <Route path="/diagnostics/:id/responses" element={<DiagnosticResponsesPage />} />
                  <Route path="/diagnostics/:id/analysis" element={<DiagnosticAnalysisPage />} />
                  <Route path="/clients" element={<ClientsPage />} />
                  <Route path="/clients/:id" element={<ClientDetailPage />} />

                  {/* Admin routes */}
                  <Route
                    path="/admin/users"
                    element={
                      <AdminRoute>
                        <AdminUsersPage />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/users/:id"
                    element={
                      <AdminRoute>
                        <AdminUserDetailPage />
                      </AdminRoute>
                    }
                  />
                </Routes>
              </MainLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
}
